import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ReactComponent as MinusSvg } from "@/assets/images/minus-background.svg";
import { Trash2, ChevronUp, ChevronDown } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  getSortedRowModel,
} from "@tanstack/react-table";
import InnerProductTable from "./InnerProductTable";
import { BuyerGroupingTable } from "@/types/scenario";
import { calculateGroupSummary } from "../../utils";
import { ItemSheetData } from "@/types/itemsheet";
import { Button } from "@/components/ui/button";
import { ReactComponent as CirclrUpSvg } from "@/assets/images/circle-triangle-up.svg";
import { ReactComponent as CirclrDownSvg } from "@/assets/images/circle-triangle-down.svg";
import { DEFAULT_PAGINATION } from "@/consts/common";
import {
  useIsFinancialManager,
  useIsAdmin,
} from "@/hooks/useIsFinancialManager";
import useUserStore from "@/stores/User";
import { CurrentUserContent } from "@/types/setting";

interface Props {
  data: BuyerGroupingTable[];
  onDiscountRateChange: (buyer: string, value: number) => void;
  setData: React.Dispatch<React.SetStateAction<BuyerGroupingTable[]>>;
  readonly?: boolean;
  isApprover?: boolean; // 승인자 여부를 props로 받음
}

export default function ScenarioGroupTable({
  data,
  onDiscountRateChange,
  setData,
  readonly = false,
  isApprover = false,
}: Props) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [sorting, setSorting] = useState<SortingState>([]);

  const isFinancialManager = useIsFinancialManager();
  const isAdmin = useIsAdmin();
  const storedUser: CurrentUserContent = useUserStore((state) => state.user);

  // 이제 divisions에서 이미 분리되어 있으므로 그대로 사용
  const processedData = data;

  // 권한 체크 로직 개선
  // 1. Finance Manager OR Administrator: 모든 값 확인 가능
  // 2. 일반 유저 & 승인자: 모든 값 확인 가능
  // 3. 일반 유저 & 승인자 아님: TPP만 마진 정보 확인 가능
  const canViewAllData = isFinancialManager || isAdmin || isApprover;
  const shouldShowTPPOnly = !canViewAllData;

  const handleRemoveDivision = (buyerToRemove: string) => {
    setData((prev) => {
      const next = [...prev];

      const removeIndex = next.findIndex((g) => g.buyer === buyerToRemove);
      if (removeIndex === -1) return prev;

      const removedGroup = next[removeIndex];
      let fallbackGroup = next.find((g) => g.buyer === "미지정");

      const newNext = next.filter((_, i) => i !== removeIndex);

      // 삭제된 그룹에 아이템이 있는 경우에만 '미지정' 그룹 처리
      if (removedGroup.items.length > 0) {
        if (fallbackGroup) {
          fallbackGroup.items.push(
            ...removedGroup.items.map((item) => ({
              ...item,
              buyer: "미지정",
              originalBuyer: (item as any).originalBuyer || item.buyer,
            }))
          );
          fallbackGroup.summary = calculateGroupSummary(
            fallbackGroup.items,
            fallbackGroup.discountRate
          );
          return [...newNext];
        } else {
          return [
            ...newNext,
            {
              buyer: "미지정",
              discountRate: 0,
              items: removedGroup.items.map((item) => ({
                ...item,
                buyer: "미지정",
                originalBuyer: (item as any).originalBuyer || item.buyer,
              })),
              summary: calculateGroupSummary(removedGroup.items, 0),
            },
          ];
        }
      }

      return newNext;
    });
  };

  const handleCostChange = (item: ItemSheetData, newCost: number, groupBuyer: string) => {
    setData((prev) => {
      const next = [...prev];

      // uuid와 buyer를 모두 확인하여 정확한 그룹의 아이템만 수정
      const groupIndex = next.findIndex((group) => group.buyer === groupBuyer);

      if (groupIndex === -1) return prev;

      const group = next[groupIndex];
      const updatedItems = group.items.map((i) =>
        i.uuid === item.uuid
          ? {
              ...i,
              total_cost: newCost,
              unit_cost: newCost / (i.quantity || 1),
            }
          : i
      );

      next[groupIndex] = {
        ...group,
        items: updatedItems,
        summary: calculateGroupSummary(updatedItems, group.discountRate),
      };

      return next;
    });
  };

  const toggleRow = (buyer: string) => {
    setExpandedRows((prev) => ({ ...prev, [buyer]: !prev[buyer] }));
  };

  const totalSummary = useMemo(() => {
    const summary = processedData.reduce(
      (acc, curr) => {
        acc.totalListPrice += curr.summary.totalListPrice;
        acc.supplyPrice += curr.summary.supplyPrice;

        // 합계는 항상 모든 데이터를 포함해야 함 (화면 표시만 권한에 따라 마스킹)
        acc.totalCost += curr.summary.totalCost;
        acc.margin += curr.summary.margin;

        acc.count += 1;
        return acc;
      },
      {
        totalListPrice: 0,
        supplyPrice: 0,
        totalCost: 0,
        margin: 0,
        count: 0,
        marginRate: 0,
        discountRate: 0,
      }
    );

    const revenue = summary.totalCost + summary.margin;
    summary.marginRate = revenue > 0 ? (summary.margin / revenue) * 100 : 0;

    summary.discountRate =
      summary.totalListPrice > 0
        ? ((summary.totalListPrice - summary.supplyPrice) /
            summary.totalListPrice) *
          100
        : 0;

    return summary;
  }, [processedData]);

  const columns = useMemo(
    () =>
      createScenarioTableColumns(
        readonly,
        expandedRows,
        canViewAllData,
        shouldShowTPPOnly,
        toggleRow,
        onDiscountRateChange,
        handleRemoveDivision,
        sorting,
        setSorting
      ),
    [
      readonly,
      onDiscountRateChange,
      handleRemoveDivision,
      canViewAllData,
      shouldShowTPPOnly,
      sorting,
    ]
  );

  const table = useReactTable({
    data: processedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    initialState: {
      pagination: {
        pageSize: DEFAULT_PAGINATION.limit,
      },
    },
  });

  const handleMoveItem = (item: ItemSheetData, toBuyer: string) => {
    setData((prev) => {
      const next = [...prev];

      const fromGroup = next.find((g) =>
        g.items.some((i) => i.uuid === item.uuid)
      );
      let toGroup = next.find((g) => g.buyer === toBuyer);

      if (!fromGroup) return prev;

      // 대상 그룹이 없으면 생성 (주로 '미지정' 그룹인 경우)
      if (!toGroup) {
        toGroup = {
          buyer: toBuyer,
          discountRate: 0,
          items: [],
          summary: calculateGroupSummary([], 0),
        };
        next.push(toGroup);
      }

      fromGroup.items = fromGroup.items.filter((i) => i.uuid !== item.uuid);

      const movedItem = {
        ...item,
        buyer: toBuyer,
        originalBuyer: (item as any).originalBuyer || item.buyer,
      };

      toGroup.items = [...toGroup.items, movedItem];

      fromGroup.summary = calculateGroupSummary(
        fromGroup.items,
        fromGroup.discountRate
      );
      toGroup.summary = calculateGroupSummary(
        toGroup.items,
        toGroup.discountRate
      );

      return [...next];
    });
  };

  const divisionNames = processedData.map((d) => d.buyer);

  return (
    <div className="border rounded-lg flex flex-col w-full h-full shadow-lg">
      <Table
        style={{
          tableLayout: "fixed",
          width: "100%",
        }}
      >
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className="px-2 py-[10px] text-sm text-white bg-dark-red-60 first:rounded-tl-md last:rounded-tr-md whitespace-nowrap"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            <>
              {table.getRowModel().rows.map((row) => {
                const buyerKey = row.original.buyer;
                const isExpanded = expandedRows[buyerKey];

                return (
                  <React.Fragment key={row.id}>
                    <TableRow data-state={row.getIsSelected() && "selected"}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className="px-2 py-[10px] whitespace-nowrap"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={columns.length} className="p-0">
                          <InnerProductTable
                            items={row.original.items}
                            onMoveItem={handleMoveItem}
                            onCostChange={(item, newCost) => handleCostChange(item, newCost, row.original.buyer)}
                            divisionOptions={divisionNames}
                            discountRate={row.original.discountRate}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              {table.getRowModel().rows.length > 0 && (
                <TableRow className="h-14 bg-white font-semibold">
                  <TableCell className="px-2 py-[10px] text-xs text-left whitespace-nowrap">
                    합계
                  </TableCell>
                  <TableCell className="px-2 py-[10px] text-xs text-left whitespace-nowrap">
                    {totalSummary.discountRate.toFixed(1)}%
                  </TableCell>
                  <TableCell className="px-2 py-[10px] text-xs text-left whitespace-nowrap">
                    {totalSummary.totalListPrice.toLocaleString()}
                  </TableCell>
                  <TableCell className="px-2 py-[10px] text-xs text-left whitespace-nowrap">
                    {totalSummary.supplyPrice.toLocaleString()}
                  </TableCell>
                  {(canViewAllData || shouldShowTPPOnly) && (
                    <>
                      <TableCell className="px-2 py-[10px] text-xs text-left whitespace-nowrap">
                        {totalSummary.totalCost.toLocaleString()}
                      </TableCell>
                      <TableCell className="px-2 py-[10px] text-xs text-left whitespace-nowrap">
                        {totalSummary.margin.toLocaleString()}
                      </TableCell>
                      <TableCell className="px-2 py-[10px] text-xs text-left whitespace-nowrap">
                        {totalSummary.marginRate.toFixed(1)}%
                      </TableCell>
                    </>
                  )}
                  {!readonly && <TableCell />}
                </TableRow>
              )}
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                데이터가 없습니다.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
export function createScenarioTableColumns(
  readonly: boolean,
  expandedRows: Record<string, boolean>,
  canViewAllData: boolean,
  shouldShowTPPOnly: boolean,
  toggleRow: (buyer: string) => void,
  onDiscountRateChange?: (buyer: string, value: number) => void,
  onRemoveDivision?: (buyer: string) => void,
  sorting?: SortingState,
  setSorting?: React.Dispatch<React.SetStateAction<SortingState>>
): ColumnDef<BuyerGroupingTable>[] {
  const baseColumns: ColumnDef<BuyerGroupingTable>[] = [
    {
      accessorKey: "buyer",
      header: () => <span className="text-sm font-semibold">디비전</span>,
      cell: ({ row }) => (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-gray-A font-normal">
            {row.original.buyer}
          </span>
          {!readonly && (
            <button onClick={() => toggleRow(row.original.buyer)}>
              <CirclrUpSvg
                className={`w-4 h-4 transition-transform duration-200 ${
                  expandedRows[row.original.buyer] ? "hidden" : "block"
                }`}
              />
              <CirclrDownSvg
                className={`w-4 h-4 transition-transform duration-200 ${
                  expandedRows[row.original.buyer] ? "block" : "hidden"
                }`}
              />
            </button>
          )}
        </div>
      ),
      size: 144,
    },

    {
      accessorKey: "discountRate",
      header: () => <span className="text-sm font-semibold">할인율</span>,
      cell: ({ row }) => {
        const buyer = row.original.buyer;
        return readonly ? (
          <span className="text-xs">
            {row.original.discountRate?.toFixed(1)}%
          </span>
        ) : (
          <DiscountRateCell
            value={row.original.discountRate}
            onChange={(newRate) => onDiscountRateChange?.(buyer, newRate)}
          />
        );
      },
      size: 115,
    },
    {
      id: "totalListPrice",
      header: () => <span className="text-sm font-semibold">판매가</span>,
      cell: ({ row }) => (
        <span className="text-xs">
          {parseFloat(
            String(row.original.summary.totalListPrice)
          ).toLocaleString()}
        </span>
      ),
      size: 115,
    },
    {
      id: "supplyPrice",
      header: () => <span className="text-sm font-semibold">제안가격</span>,
      cell: ({ row }) => (
        <span className="text-xs">
          {row.original.summary.supplyPrice.toLocaleString()}
        </span>
      ),
      size: 115,
    },
    ...(shouldShowTPPOnly || canViewAllData
      ? [
          {
            id: "totalCost",
            header: () => (
              <span className="text-sm font-semibold">매입가/원가</span>
            ),
            cell: ({ row }: { row: any }) => (
              <span className="text-xs">
                {shouldShowTPPOnly && row.original.buyer !== "TPP"
                  ? ""
                  : row.original.summary.totalCost.toLocaleString()}
              </span>
            ),
            size: 115,
          },
          {
            id: "margin",
            header: () => (
              <span className="text-sm font-semibold">마진금액</span>
            ),
            cell: ({ row }: { row: any }) => (
              <span className="text-xs">
                {shouldShowTPPOnly && row.original.buyer !== "TPP"
                  ? ""
                  : row.original.summary.margin.toLocaleString()}
              </span>
            ),
            size: 115,
          },

          {
            id: "marginRate",
            header: ({ column }: { column: any }) => (
              <div className="flex items-center gap-1">
                <span className="text-sm font-semibold">마진율</span>
                <button
                  onClick={() =>
                    column.toggleSorting(column.getIsSorted() === "asc")
                  }
                  className="flex items-center hover:text-gray-300"
                >
                  {column.getIsSorted() === "asc" ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : column.getIsSorted() === "desc" ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <div className="flex flex-col">
                      <ChevronUp className="w-3 h-3 -mb-1" />
                      <ChevronDown className="w-3 h-3" />
                    </div>
                  )}
                </button>
              </div>
            ),
            accessorFn: (row: BuyerGroupingTable) => row.summary.marginRate,
            cell: ({ row }: { row: any }) => (
              <span className="text-xs">
                {shouldShowTPPOnly && row.original.buyer !== "TPP"
                  ? ""
                  : `${row.original.summary.marginRate.toFixed(1)}%`}
              </span>
            ),
            size: 115,
          },
        ]
      : []),
  ];

  if (!readonly) {
    baseColumns.push({
      id: "actions",
      header: () => <span className="text-sm font-semibold"></span>,
      cell: ({ row }) => {
        if (!row.original.isCustomGroup) return null;
        return (
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onRemoveDivision?.(row.original.buyer)}
            className="w-4 h-4"
          >
            <MinusSvg className="text-red-500" />
          </Button>
        );
      },
      size: 40,
    });
  }

  return baseColumns;
}

const DiscountRateCell = ({
  value,
  onChange,
}: {
  value: number;
  onChange: (newRate: number) => void;
}) => {
  const [localValue, setLocalValue] = useState(String(value));

  useEffect(() => {
    setLocalValue(String(value));
  }, [value]);

  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        min={0}
        max={100}
        value={localValue}
        className="w-12 px-1 py-[2px] border-none text-left text-xs"
        onChange={(e) => {
          setLocalValue(e.target.value);
        }}
        onBlur={() => {
          const parsed = parseFloat(localValue);
          if (!isNaN(parsed)) onChange(parsed);
        }}
      />
      <span className="text-xs text-gray-A font-normal">%</span>
    </div>
  );
};
